import React, { useEffect, useMemo, useState } from "react";
import { Edit2, Plus, RefreshCcw, Trash2Icon, X } from "lucide-react";
import toast from "react-hot-toast";
import { api } from "../../services/api";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";

type SubscriptionPlan = {
  id: string;
  planName: string;
  price: string;
  billingCadence: string;
  highlight: string;
  features: string[];
  popular: boolean;
  active: boolean;
};

const emptyForm = {
  planName: "",
  price: "",
  billingCadence: "per month",
  highlight: "",
  features: "",
  popular: false,
  active: true,
};

const normalizePlan = (raw: any): SubscriptionPlan => {
  const features = Array.isArray(raw?.features)
    ? raw.features
    : typeof raw?.features === "string"
      ? raw.features.split(/[,\n]/g)
      : [];

  return {
    id: raw?.id || raw?._id || "",
    planName: raw?.planName || "",
    price: raw?.price || "",
    billingCadence: raw?.billingCadence || raw?.billingCadence || "per month",
    highlight: raw?.highlight || "",
    features: features.map((item: string) => item.trim()).filter(Boolean),
    popular: Boolean(raw?.popular ?? raw?.isPopular ?? false),
    active: Boolean(raw?.active ?? raw?.isActive ?? true),
  };
};

const SubscriptionPlanManager: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SubscriptionPlan | null>(
    null,
  );
  const [error, setError] = useState("");

  const fetchPlans = async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/backend/subscriptionPlans");
      const payload =
        res?.data?.plans ||
        res?.data?.subscriptionPlans ||
        res?.data?.data ||
        res?.data ||
        [];
      const list = Array.isArray(payload) ? payload : [];
      setPlans(list.map(normalizePlan));
    } catch (err: any) {
      toast.error(err?.message || "Failed to load subscription plans");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const featurePreview = useMemo(
    () =>
      plans.map((plan) => ({
        id: plan.id,
        text:
          plan.features.length > 0
            ? plan.features.slice(0, 2).join(", ") +
              (plan.features.length > 2 ? ` +${plan.features.length - 2}` : "")
            : "—",
      })),
    [plans],
  );

  const openAddModal = () => {
    setIsEditMode(false);
    setEditingPlanId(null);
    setFormData({ ...emptyForm });
    setError("");
    setIsModalOpen(true);
  };

  const openEditModal = (plan: SubscriptionPlan) => {
    setIsEditMode(true);
    setEditingPlanId(plan.id);
    setFormData({
      planName: plan.planName,
      price: plan.price,
      billingCadence: plan.billingCadence,
      highlight: plan.highlight,
      features: plan.features.join("\n"),
      popular: plan.popular,
      active: plan.active,
    });
    setError("");
    setIsModalOpen(true);
  };

  const parseFeatures = (value: string) =>
    value
      .split(/[,\n]/g)
      .map((item) => item.trim())
      .filter(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.planName.trim()) {
      setError("Plan name is required.");
      return;
    }
    if (!formData.price.trim()) {
      setError("Price is required.");
      return;
    }

    const features = parseFeatures(formData.features);

    if (features.length === 0) {
      setError("Please add at least one feature.");
      return;
    }

    const payload = {
      planName: formData.planName.trim(),
      price: formData.price.trim(),
      billingCadence: formData.billingCadence.trim() || "per month",
      highlight: formData.highlight.trim(),
      features,
      popular: formData.popular,
      active: formData.active,
      isPopular: formData.popular,
      isActive: formData.active,
    };

    setIsSaving(true);
    try {
      if (isEditMode && editingPlanId) {
        await api.put(
          `/backend/subscriptionPlans/${editingPlanId}/update`,
          payload,
        );
        toast.success("Plan updated");
      } else {
        await api.post("/backend/subscriptionPlans/add", payload);
        toast.success("Plan created");
      }
      setIsModalOpen(false);
      fetchPlans();
    } catch (err: any) {
      toast.error(err?.message || "Failed to save plan");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/backend/subscriptionPlans/${deleteTarget.id}/delete`);
      toast.success("Plan deleted");
      setDeleteTarget(null);
      fetchPlans();
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete plan");
    }
  };

  const togglePopular = async (plan: SubscriptionPlan) => {
    try {
      const updated = {
        ...plan,
        popular: !plan.popular,
        isPopular: !plan.popular,
      };
      await api.put(`/backend/subscriptionPlans/${plan.id}/update`, updated);
      fetchPlans();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update popular flag");
    }
  };

  const toggleActive = async (plan: SubscriptionPlan) => {
    try {
      const updated = {
        ...plan,
        active: !plan.active,
        isActive: !plan.active,
      };
      await api.put(`/backend/subscriptionPlans/${plan.id}/update`, updated);
      fetchPlans();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update active flag");
    }
  };

  return (
    <div className="bg-white">
      <div className="p-6 border-b border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Subscription Plans
            </h3>
            <p className="text-sm text-gray-500">
              Add, edit, and manage subscription pricing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchPlans}
              className="h-10 w-10 rounded-lg border border-gray-200 hover:bg-gray-50 transition inline-flex items-center justify-center"
              title="Refresh"
            >
              <RefreshCcw className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={openAddModal}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" />
              Add Plan
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading plans...</div>
        ) : plans.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-base">No subscription plans found.</p>
            <button
              onClick={openAddModal}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:border-gray-300 transition"
            >
              <Plus className="w-4 h-4" />
              Create your first plan
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Plan
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Price
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Cadence
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Highlight
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Features
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Popular
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Active
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-semibold text-gray-900">
                        {plan.planName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {plan.price}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {plan.billingCadence || "—"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        {plan.highlight || "—"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        {plan.features.length > 0
                          ? plan.features.join(", ")
                          : "—"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => togglePopular(plan)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                          plan.popular ? "bg-indigo-600" : "bg-gray-200"
                        }`}
                        role="switch"
                        aria-checked={plan.popular}
                        title="Toggle popular"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                            plan.popular ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleActive(plan)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                          plan.active ? "bg-emerald-500" : "bg-gray-200"
                        }`}
                        role="switch"
                        aria-checked={plan.active}
                        title="Toggle active"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                            plan.active ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(plan)}
                          className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-gray-50 transition inline-flex items-center justify-center"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(plan)}
                          className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-red-50 transition inline-flex items-center justify-center"
                          title="Delete"
                        >
                          <Trash2Icon className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setIsModalOpen(false)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-xl">
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute right-4 top-4 rounded-lg p-1 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {isEditMode ? "Edit plan" : "Add plan"}
                </h3>
                <p className="text-sm text-gray-500">
                  Fill in the plan details below.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="px-6 py-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Plan name
                    </label>
                    <input
                      value={formData.planName}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          planName: e.target.value,
                        }))
                      }
                      placeholder="e.g. Seeker"
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Price
                    </label>
                    <input
                      value={formData.price}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          price: e.target.value,
                        }))
                      }
                      placeholder="$19"
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Billing cadence
                    </label>
                    <input
                      value={formData.billingCadence}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          billingCadence: e.target.value,
                        }))
                      }
                      placeholder="per month"
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Highlight
                    </label>
                    <input
                      value={formData.highlight}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          highlight: e.target.value,
                        }))
                      }
                      placeholder="Short summary of the plan"
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Features
                    </label>
                    <textarea
                      value={formData.features}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          features: e.target.value,
                        }))
                      }
                      placeholder="Comma or line separated features"
                      rows={4}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                    />
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={formData.popular}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          popular: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Mark as popular
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          active: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Active
                  </label>
                </div>

                {error && (
                  <div className="mt-4 text-sm text-red-600">{error}</div>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSaving ? "Saving..." : "Save plan"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="rounded-xl border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-600 transition hover:border-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Plan"
        message="Are you sure you want to delete this plan?"
        itemName={deleteTarget?.planName}
      />
    </div>
  );
};

export default SubscriptionPlanManager;
